import { Plus, X } from 'lucide-react'

import { Badge } from '@/shared/components/atoms/Badge/Badge'
import { Button } from '@/shared/components/atoms/Button'

interface AssignedGroupsProps<T extends { id: number; name: string }> {
	title: string
	items: T[]
	onAdd?: () => void
	onRemove?: (itemId: number) => void
	addButtonText?: string
	emptyMessage?: string
}

export const AssignedGroups = <T extends { id: number; name: string }>({
	title,
	items,
	onAdd,
	onRemove,
	addButtonText = 'Add item',
	emptyMessage,
}: AssignedGroupsProps<T>) => {
	return (
		<div className="mb-6 md:mb-12">
			<h2 className="text-sm md:text-xl font-medium md:font-semibold mb-2">
				{title}
			</h2>

			<div className="flex flex-wrap gap-2">
				{items.length === 0 && emptyMessage ? (
					<p className="text-sm text-muted-foreground">{emptyMessage}</p>
				) : (
					items.map((item) => (
						<Badge
							key={item.id}
							variant="outline"
							className="bg-shade rounded-full px-3 py-1.5 text-xs md:text-sm/1 hover:bg-shade/10 transition-colors"
						>
							<span>{item.name}</span>
							{onRemove && (
								<button
									onClick={(e) => {
										e.stopPropagation()
										onRemove(item.id)
									}}
									className="ml-2 opacity-70 hover:opacity-100 transition-opacity"
								>
									<X className="h-4 w-4" />
								</button>
							)}
						</Badge>
					))
				)}

				{onAdd && (
					<Button
						variant="outline"
						size="sm"
						onClick={onAdd}
						className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 w-auto md:w-auto font-sm bg-transparent rounded-full border-dashed px-3 py-1.5 text-sm/1 border text-muted-foreground hover:text-foreground hover:border-foreground/30 hover:bg-shade cursor-pointer"
					>
						{addButtonText}
						<Plus className="h-3 w-3" />
					</Button>
				)}
			</div>
		</div>
	)
}
